export type EisWindowType = 'YearToDate' | 'RollingDays';

export interface EisRuleTemplate {
  id: number;
  name: string;
}

export interface EisRuleActiveVersion {
  id: number;
  thresholdPoints: number;
  windowType: EisWindowType;
  windowDays?: number | null;
}

export interface EisRuleListItem {
  id: number;
  organizationId: string;
  name: string;
  description?: string | null;
  ruleType: 'Frequency' | 'Scoring';
  versionStatus: string;
  versionId: number;
  activeVersion?: EisRuleActiveVersion | null;
  template?: EisRuleTemplate | null;
  flagsCount?: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface EisRulesResponse {
  content: EisRuleListItem[];
  count: number;
  nextToken: string | null;
}

export type EisRulesListParams = {
  search?: string | null;
  versionStatus?: string | null;
  organizationId?: string | null;
};

export interface EisRuleFrequency {
  incidentCount: number | null;
  windowDays: number | null;
}

export interface CreateEisRuleRequest {
  name: string;
  description?: string;
  templateId: number;
  organizationId: string;
  ruleType?: 'Frequency' | 'Scoring';
}

export interface UpdateEisRuleRequest {
  name: string;
  description?: string;
  templateId: number;
  organizationId: string;
  ruleType: 'Frequency' | 'Scoring';
}

export interface SaveEisRuleConfigurationRequest {
  rule: EisRuleConfigurationRule;
  scoring: { templates: EisConfigScoringTemplate[] };
  threshold: EisRuleThreshold | Record<string, never>;
  notifications: EisRuleNotifications | Record<string, never>;
  frequency?: EisRuleFrequency;
}

export interface UpdateEisFlagRequest {
  status?: string;
  disposition?: string;
  correctiveActions: string[];
  comments?: string;
}

export type EisScoredFieldType =
  | 'Text'
  | 'Number'
  | 'Boolean'
  | 'Date'
  | 'Time'
  | 'SingleSelect'
  | 'MultiSelect'
  | 'Lookup'
  | 'Attachment'
  | 'BodyImage'
  | 'VehicleImage'
  | 'Narrative';

export type EisScoringStatus = 'Scored' | 'NotScored' | 'PartiallyScored';

export interface EisScoredFieldScore {
  id: number;
  predicateType: string;
  predicateJson: string;
  points?: number | null;
}

export interface EisScoredFieldField {
  id: number;
  type: string;
  label: string;
}

export interface EisScoredFieldItem {
  id: number;
  ruleId: number;
  field: EisScoredFieldField;
  templateSource: EisRuleTemplate;
  scores: EisScoredFieldScore[];
  status: EisScoringStatus;
}

export interface EisScoredFieldsResponse {
  content: EisScoredFieldItem[];
  count: number;
  nextToken: string | null;
}

// Scoring configuration types (Rule Configuration page)
export type EisScoringFieldType = 'Number' | 'Boolean' | 'SingleSelect' | 'MultiSelect';

export type EisScoringPredicateType = 'NumberRange' | 'Boolean' | 'OptionMatch';

export interface EisScoringScore {
  id: number;
  predicateType: EisScoringPredicateType;
  predicateJson: Record<string, unknown>;
  points: number | null;
  label?: string;
}

export interface EisScoringIndicator {
  id: number;
  templateId: number;
  fieldId: number;
  fieldName: string;
  fieldType: EisScoringFieldType;
  scores: EisScoringScore[];
  status: EisScoringStatus;
}

export interface EisScoringFieldsResponse {
  indicators: EisScoringIndicator[];
}

export const EIS_SCORABLE_FIELD_TYPES = ['Number', 'Boolean', 'SingleSelect', 'MultiSelect'] as const;

// Rule Configuration endpoint types (GET /rules/{id}/configuration)

export interface EisRuleConfigurationRule {
  id: number;
  organizationId: string;
  name: string;
  description?: string | null;
  versionId: number;
  versionStatus?: 'Draft' | 'Active' | 'Inactive';
  ruleType?: 'Frequency' | 'Scoring';
}

export interface EisConfigScoringScore {
  id: number;
  predicateType: EisScoringPredicateType;
  predicateJson: Record<string, unknown>;
  points: number | null;
  label?: string;
}

export interface EisConfigScoringIndicator {
  id: number;
  templateId: number;
  fieldId: number;
  scores: EisConfigScoringScore[];
  status: EisScoringStatus;
  fieldName: string;
  fieldType: EisScoringFieldType;
  options?: { fieldOptionId: number; label: string; rank?: number }[];
}

export interface EisConfigScoringTemplate {
  id: number;
  templateId: number;
  templateName: string;
  indicators: EisConfigScoringIndicator[];
}

export interface EisRuleThreshold {
  scoreThreshold: number | null;
  windowDays: 30 | 60 | 90 | 180 | 365 | null;
}

export interface EisNotificationRecipient {
  id: string;
  name: string;
  email: string;
}

export interface EisRuleNotifications {
  notifySupervisors: boolean;
  notifyAdditional: boolean;
  additionalRecipients: EisNotificationRecipient[];
}

export interface EisRuleConfigurationResponse {
  rule: EisRuleConfigurationRule;
  scoring: {
    templates: EisConfigScoringTemplate[];
  };
  threshold: EisRuleThreshold | Record<string, never>;
  notifications: EisRuleNotifications | Record<string, never>;
  frequency?: EisRuleFrequency | Record<string, never>;
}

export type EisFlagStatus = 'Active' | 'Closed' | 'Cleared';

export interface EisFlaggedPersonnelItem {
  id: number;
  userId: string;
  name: string;
  flaggedDate: string;
  groups: string;
  totalRiskScore: number;
  overThresholdBy: number;
  status: EisFlagStatus;
}

export interface EisFlaggedPersonnelResponse {
  content: EisFlaggedPersonnelItem[];
  count: number;
  nextToken: string | null;
}

export interface EisFlagDetails {
  status: EisFlagStatus;
  triggeredRule: string;
  flaggedDate: string;
  ruleThreshold: number;
  flaggedScore: number;
  timeWindow: number;
  // flag resolution (backend not implemented yet, will be returned by GET flag endpoint)
  disposition?: string | null;
  correctiveActions?: string[];
  comments?: string | null;
}

export interface EisFlaggedPersonnelDetails {
  id: string;
  name: string;
  groups: string;
}

export interface EisFlagIncident {
  caseNumber: string;
  template: string;
  incidentDate: string;
  riskScore: number;
}

export interface EisFlagIncidentsResponse {
  content: EisFlagIncident[];
  count: number;
}
